import { useCallback, useState } from "react";
import {
  linkDiscoveredMeeting,
  onboardAndImportMeeting,
} from "../services/meetings.service";
import { useAlert } from "../functions/useAlert";
import { CalendarEvent } from "./useCalendarEvents";

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/**
 * Promotes a calendar event into a Prospo meeting against a chosen client.
 *
 * The backend attaches only attendees already linked to that client and rejects
 * the import when none match, so that case gets a message the advisor can act on
 * rather than a bare failure.
 */
export function useAddEventToProspo(onAdded?: () => void) {
  const [setAlert] = useAlert();
  const [pendingEvent, setPendingEvent] = useState<CalendarEvent | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  const requestAdd = useCallback((event: CalendarEvent) => setPendingEvent(event), []);
  const cancel = useCallback(() => setPendingEvent(null), []);

  /**
   * True when the pending event needs the create-contact/create-client wizard
   * rather than the plain client picker.
   */
  const needsOnboarding = pendingEvent?.shape === "oneToOneUnknown";

  /** Wizard commit: create the contact and client, then import in one call. */
  const confirmOnboard = useCallback(
    async (
      event: CalendarEvent,
      details: { contactName: string; contactEmail: string; clientName: string; entityType: string },
    ) => {
      // Either shape is valid: a discovered calendar event carries `raw` and gets
      // imported; an unassigned Calendly booking is already a Prospo meeting and
      // only needs a client attached.
      if (!event?.raw && !event?.meeting?._id) return;
      setSubmitting(true);
      setAddingId(event.id);
      try {
        const res: any = await onboardAndImportMeeting({
          event: event.raw,
          meetingId: event.meeting?._id ? String(event.meeting._id) : undefined,
          ...details,
          timezone: BROWSER_TZ,
        });

        if (!res?.success) {
          setAlert(res?.message || "Could not add this meeting.", "error");
          return;
        }

        setAlert(`${details.clientName} created and meeting added.`, "success");
        setPendingEvent(null);
        onAdded?.();
      } catch (err: any) {
        setAlert(err?.message || "Could not add this meeting.", "error");
      } finally {
        setSubmitting(false);
        setAddingId(null);
      }
    },
    [setAlert, onAdded],
  );

  const confirmAdd = useCallback(
    async (event: CalendarEvent, clientId: string) => {
      if (!event?.raw || !clientId) return;
      setSubmitting(true);
      setAddingId(event.id);
      try {
        const res: any = await linkDiscoveredMeeting({
          event: event.raw,
          clientId,
          timezone: BROWSER_TZ,
        });

        if (!res?.success) {
          setAlert(
            res?.message
              || "Could not add this meeting. Check that an attendee is linked to this client.",
            "error",
          );
          return;
        }

        setAlert(
          res.alreadyImported ? "This meeting is already in Prospo." : "Meeting added to Prospo.",
          "success",
        );
        setPendingEvent(null);
        onAdded?.();
      } catch (err: any) {
        setAlert(err?.message || "Could not add this meeting.", "error");
      } finally {
        setSubmitting(false);
        setAddingId(null);
      }
    },
    [setAlert, onAdded],
  );

  return {
    pendingEvent,
    submitting,
    addingId,
    needsOnboarding,
    requestAdd,
    cancel,
    confirmAdd,
    confirmOnboard,
  };
}
